/**
 * Pure helpers for the Subscription tab: which plan card is the user's, and
 * where "Manage billing" should send them.
 *
 * RevenueCat Web's `managementURL` only exists for subscriptions bought
 * through RC Web Billing. A Pro subscription bought in the Android app
 * (Play Store), on iOS, or granted by an admin (promotional) has no web
 * portal, so the tab used to show "Не удалось открыть портал оплаты" for
 * those users. Each store has its own place to manage the subscription.
 */
import type { Subscription } from '../api/subscriptionService';

export type PlanInterval = 'monthly' | 'annual';

/** RevenueCat's store names (`Store` in @revenuecat/purchases-js). */
export type RcStore =
    | 'app_store' | 'mac_app_store' | 'play_store' | 'amazon' | 'stripe'
    | 'rc_billing' | 'promotional' | 'paddle' | 'test_store' | 'galaxy' | 'unknown';

export const PLAY_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions?package=org.weavewords.app';
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

/** Days left in the period above which a subscription must be annual. */
const ANNUAL_THRESHOLD_DAYS = 40;

/**
 * Monthly or annual from a RevenueCat product id, matched against the
 * offering's own product ids first, then by name. Null if it can't tell.
 */
export function intervalFromProductId(
    productId: string | null | undefined,
    known: { monthly?: string | null; annual?: string | null } = {},
): PlanInterval | null {
    if (!productId) return null;
    if (known.annual && productId === known.annual) return 'annual';
    if (known.monthly && productId === known.monthly) return 'monthly';
    const id = productId.toLowerCase();
    if (/annual|year|yearly|12m|p1y/.test(id)) return 'annual';
    if (/month|monthly|1m\b|p1m/.test(id)) return 'monthly';
    return null;
}

/**
 * Last-resort hint from the period end: more than ~40 days left can only be an
 * annual period. Fewer days left says nothing (an annual plan near renewal looks
 * the same as a monthly one), so that, and a missing date, are null.
 */
export function intervalFromPeriodEnd(periodEnd: string | null | undefined, now: Date = new Date()): PlanInterval | null {
    if (!periodEnd) return null;
    const end = new Date(periodEnd).getTime();
    if (Number.isNaN(end)) return null;
    const daysLeft = (end - now.getTime()) / 86_400_000;
    return daysLeft > ANNUAL_THRESHOLD_DAYS ? 'annual' : null;
}

/** The store, from RevenueCat if known, else from the backend's `provider`. */
export function resolveStore(rcStore: RcStore | null | undefined, provider: Subscription['provider'] | undefined): RcStore {
    if (rcStore) return rcStore;
    switch (provider) {
        case 'google': return 'play_store';
        case 'apple': return 'app_store';
        case 'web': return 'rc_billing';
        default: return 'unknown';
    }
}

/** The RevenueCat side of the Subscription tab: still loading, failed, or answered. */
export type RcLookup =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ok'; plan: RcPlanInfo };

/** Structural copy of purchaseService's ActivePlan (kept here so this module stays pure). */
export interface RcPlanInfo {
    entitled: boolean;
    store: RcStore | null;
    interval: PlanInterval | null;
    managementURL: string | null;
}

export interface ManagementTarget {
    /** 'promotional' means "nothing to manage" (a grant, or RC confirmed there's no subscription). */
    store: RcStore;
    /** Where Manage billing goes now, or null (none, or not known yet — retry on click). */
    manageUrl: string | null;
}

/**
 * Decides where "Manage billing" leads for a Pro user, from RevenueCat's answer plus the
 * backend's `provider` (who sold the subscription, per RC's webhook):
 * - RC's entitlement wins when there is one: its store, and the portal for web purchases.
 * - The RC web portal is only used without an entitlement when the backend says the purchase
 *   was made on web — a leftover portal from an old web purchase must not override a Play
 *   subscription or an admin grant.
 * - "Nothing to manage" (store 'promotional') only when RC actually answered: no entitlement,
 *   no usable portal, and not a Play/App Store purchase (managed in the store regardless). An
 *   RC error or a build without the RC key is NOT that — a paying subscriber would lose their
 *   billing buttons on a network blip — so those fall back to the backend's provider.
 */
export function resolveManagement(lookup: RcLookup, provider: Subscription['provider'] | undefined): ManagementTarget {
    const plan = lookup.status === 'ok' ? lookup.plan : null;
    const portal = plan && (plan.entitled || provider === 'web') ? plan.managementURL : null;
    if (plan?.entitled && plan.store) {
        return { store: plan.store, manageUrl: manageBillingUrl(plan.store, portal) };
    }
    const storeManaged = provider === 'google' || provider === 'apple';
    if (plan && !portal && !storeManaged) {
        return { store: 'promotional', manageUrl: null };
    }
    const store = resolveStore(undefined, provider);
    return { store, manageUrl: manageBillingUrl(store, portal) };
}

/**
 * Where "Manage billing" goes for this store, or null when there is nothing
 * the user can manage (promotional grant, or a web purchase whose portal URL
 * RevenueCat didn't return).
 */
export function manageBillingUrl(store: RcStore, rcManagementUrl: string | null | undefined): string | null {
    switch (store) {
        case 'play_store':
            return PLAY_SUBSCRIPTIONS_URL;
        case 'app_store':
        case 'mac_app_store':
            return APPLE_SUBSCRIPTIONS_URL;
        case 'promotional':
            return null;
        default:
            return rcManagementUrl || null;
    }
}
